import React, { useState } from "react";
import { FaDownload, FaSpinner } from "../utils/icons";
import { useAuth } from "../context/AuthContext";
import { useReportState } from "../hooks/useStudyQueries";
import { fetchReport } from "../hooks/useReportQuery";
import { downloadReport } from "../utils/reportDownload";
import { makeDownloadableReport } from "../utils/reportDocument";

interface CaseReportDownloadButtonProps {
  studyId: string;
  compact?: boolean;
  className?: string;
}

/** Downloads one case report, including an unsigned generated draft when no
 * saved editor document exists yet. */
export const CaseReportDownloadButton: React.FC<CaseReportDownloadButtonProps> = ({
  studyId,
  compact = false,
  className = "",
}) => {
  const { token, userId } = useAuth();
  const { refetch } = useReportState(token, userId, studyId, { enabled: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setBusy(true);
    setError(null);
    try {
      const [stateResult, generated] = await Promise.all([
        refetch(),
        token ? fetchReport(studyId, token).catch(() => null) : Promise.resolve(null),
      ]);
      const report = await makeDownloadableReport(studyId, stateResult.data ?? {}, generated);
      if (!report) {
        setError(
          "A report could not be generated for this case yet. Try again after analysis completes.",
        );
        return;
      }
      await downloadReport(report);
    } catch {
      setError("Could not prepare a complete report with Impression. No PDF was downloaded.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        data-testid="case-report-download"
        className={`inline-flex items-center gap-1 rounded-xs border border-border-strong px-2 py-1 font-mono text-[9px] text-fg-muted transition-colors hover:text-fg disabled:cursor-wait disabled:opacity-60 ${className}`}
        title="Download this case's saved report as PDF"
      >
        {busy ? (
          <FaSpinner className="h-2.5 w-2.5 animate-spin" />
        ) : (
          <FaDownload className="h-2.5 w-2.5" />
        )}
        {!compact && "Export report"}
      </button>
      {error && (
        <span
          role="alert"
          className="absolute right-0 top-full z-20 mt-1 w-60 rounded-xs border border-destructive/50 bg-bg-2 p-2 text-[10px] text-destructive shadow-xl"
        >
          {error}
        </span>
      )}
    </span>
  );
};
